import { CommonModule } from '@angular/common';
import { afterNextRender, Component, computed, effect, inject, OnDestroy, OnInit, signal, untracked, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { fromEvent, Subscription, switchMap, timer } from 'rxjs';
import { ConfigService, inlineSideNavEnabled } from '@jms/ui/ui.standalone.services/config.service';

import { ApplicationActions } from '@jms/tenant/module.jms.state';
import { DomainUtility, FeatureFlags, UserType } from '@jms/model';
import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';
import { VersionService } from '@workbuddy/services/version.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { injectNavigationEnd } from 'ngxtension/navigation-end';
import { ToastService } from '@workbuddy/ui/services';
import { Command } from '@workbuddy/ui/model';
import { SideNavComponent } from '../sidenav/sidenav.component';
import { InlineSideNavComponent } from '../inline-sidenav/inline.sidenav.component';
import { ConfirmModule } from '@jms/ui/ui.confirm';
import { ToastModule } from 'primeng/toast';
import { AlertComponent } from '@workbuddy/ui/components/alert';
import { CommandPaletteHostComponent } from '@workbuddy/ui/v2/blocks/command.palette';
import { AuthorizeGuard } from '@jms/tenant/module.jms.authorisation';
import { AgentPopupChatComponent, AgentChatFooterComponent, AgentPanelService, RESERVED_TAB_ID } from '@workbuddy/ai/agent';
import { AgentUtility } from '@workbuddy/ai/agent/agent.utility';
import { AuthSessionSocketService } from '../../services/auth.session.socket.service';
import { MaintenanceSocketService } from '../../services/maintenance.socket.service';

/** Pixel geometry of the windowed agent panel while draggable/resizable. */
interface PopupRect { top: number; left: number; width: number; height: number; }

/** The eight resize-handle directions (edges + corners). */
type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

// Default windowed size, the gap it opens from the far-right/bottom, the minimum
// it can be resized to, and the safety gap kept from every viewport edge.
const POPUP_DEFAULT_WIDTH = 480;
const POPUP_DEFAULT_HEIGHT = 600;
const POPUP_MIN_WIDTH = 400;
const POPUP_MIN_HEIGHT = 480;
const POPUP_OPEN_MARGIN = 24;
const POPUP_EDGE_GAP = 8;
// Only offer "reset position" once the window is meaningfully off its default — a nudge
// of a few pixels shouldn't surface the button.
const POPUP_MOVED_THRESHOLD = 32;
// Below this viewport size the floating window has nowhere usable to live: a window at its
// minimum size would be squashed or would fight the app for space. Rather than offer one that
// can't fit, the whole windowed surface (footer bar + panel) is withdrawn — see $showBottomBar.
const POPUP_VIEWPORT_MIN_WIDTH = 640;
const POPUP_VIEWPORT_MIN_HEIGHT = 560;

/** Whether the viewport is too small to host a usable floating agent window. */
function isViewportTooSmallForPopup(): boolean {

  return window.innerWidth < POPUP_VIEWPORT_MIN_WIDTH || window.innerHeight < POPUP_VIEWPORT_MIN_HEIGHT;
}

@Component({
  selector: 'shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.less'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, RouterOutlet, SideNavComponent, InlineSideNavComponent, ConfirmModule, ToastModule, AlertComponent, AgentPopupChatComponent, AgentChatFooterComponent, CommandPaletteHostComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[attr.id]': '"shell"'
  }
})
export class ShellComponent implements OnInit, OnDestroy {

  private readonly router = inject(Router);
  private readonly authorizeGuard = inject(AuthorizeGuard);
  protected readonly agentPanelService = inject(AgentPanelService);
  public readonly actions = inject(ApplicationActions);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly versionService = inject(VersionService);
  private readonly toasts = inject(ToastService);
  private readonly authSessionSocket = inject(AuthSessionSocketService);
  private readonly maintenanceSocket = inject(MaintenanceSocketService);

  private readonly hasAiAccess = this.authorizeGuard.canLoadPath('agent');
  private readonly isAiAssistantEnabled = ConfigService.environment.featureFlags?.[FeatureFlags.AIAssistant] ?? false;
  protected readonly inlineSideNavEnabled = inlineSideNavEnabled;
  private readonly navigationEnd$ = injectNavigationEnd();
  private readonly $navigationEnd = toSignal(this.navigationEnd$);

  protected readonly $isOnAiRoute = computed(() => {
    const url = this.$navigationEnd()?.urlAfterRedirects ?? this.router.url;
    return url.startsWith('/agent');
  });
  // True while the viewport is too small to host a usable floating window. Kept as a signal
  // (updated on resize) so the surfaces that depend on it re-evaluate reactively.
  protected readonly $viewportTooSmallForPopup = signal(isViewportTooSmallForPopup());

  /**
   * Gates BOTH the footer tab bar and the floating panel wrapper.
   *
   * On a viewport too small for a usable window the whole windowed surface is withdrawn —
   * there is no point offering a chat that can't fit (the full-page /agent route is still
   * available). An already-EXPANDED panel is exempt: it fills the screen, so it stays usable
   * at any size and is never yanked out from under the user mid-conversation. Once that
   * expanded panel is closed, the small-viewport rule takes over again.
   */
  protected readonly $showBottomBar = computed(() => {

    if (!this.hasAiAccess || !this.isAiAssistantEnabled || this.$isOnAiRoute()) return false;
    if (!this.$viewportTooSmallForPopup()) return true;
    return this.agentPanelService.$displayMode() === 'pseudo' && this.agentPanelService.$panelOpen();
  });

  // Mirror onto AgentPanelService.$panelVisible so consumers (prompt input focus)
  // can react to the shell's measure-and-reveal cycle without re-implementing it.
  protected readonly $panelPositionReady = this.agentPanelService.$panelVisible;

  // ─── Windowed panel geometry (drag + resize) ────────────────────────────────
  // Explicit pixel rect for the popup while windowed. Reset to a far-right default
  // on each fresh open (so a chat always opens on the right, whichever tab launched
  // it); dragging the title bar and the edge/corner handles mutate it live.
  protected readonly $popupRect = signal<PopupRect | null>(null);
  // True while a drag/resize gesture is in flight — disables the wrapper's easing so
  // the window tracks the pointer 1:1 instead of lagging behind the transition.
  protected readonly $windowInteracting = signal(false);
  // Only apply the pixel rect in popup mode; the expanded (pseudo) surface keeps its
  // own CSS-driven geometry, and an undefined value lets those styles fall through.
  protected readonly $activePopupRect = computed(() => this.agentPanelService.$displayMode() === 'popup' ? this.$popupRect() : null);
  // True when the windowed panel has been dragged/resized off its far-right default —
  // drives the header's "reset position" button (shown only while it's out of place).
  protected readonly $popupMoved = computed(() => {

    if (this.agentPanelService.$displayMode() !== 'popup') return false;
    const rect = this.$popupRect();
    if (!rect) return false;
    const def = this.computeDefaultPopupRect();
    return Math.abs(rect.left - def.left) > POPUP_MOVED_THRESHOLD || Math.abs(rect.top - def.top) > POPUP_MOVED_THRESHOLD || Math.abs(rect.width - def.width) > POPUP_MOVED_THRESHOLD || Math.abs(rect.height - def.height) > POPUP_MOVED_THRESHOLD;
  });
  // Tracks the open→closed edge so a fresh open resets to the default rect while a
  // tab-switch on an already-open window leaves the user's drag/resize in place.
  private wasPanelOpen = false;
  private removeWindowGestureListeners: (() => void) | null = null;
  // Last-seen viewport, used on resize to reflow the window relative to the edge it sits
  // closest to (so it stays put) rather than letting its fixed top/left drift.
  private lastViewport = { vw: window.innerWidth, vh: window.innerHeight };
  // True while a drag/resize gesture is actually MOVING the window (not merely pressed on it).
  // Drives the popup's reset-hint overlay, so the hint is live for the duration of the gesture
  // and gone the moment the pointer is released.
  protected readonly $windowDragging = signal(false);

  // Gate the panel wrapper's CSS transitions until after the first paint. A fresh page
  // load mounts the wrapper inside @if ($showBottomBar()); without this it fades from
  // visible -> hidden over ~0.22s on mount — the reload "ghost" popup flash. Flipped true
  // post-render so only user-initiated opens/closes/morphs animate. (Same idiom as the
  // fullpage sidebar's $sidebarAnimate.)
  protected readonly $panelAnimate = signal(false);
  private readonly _enablePanelAnim = afterNextRender(() => this.$panelAnimate.set(true));

  protected readonly $sidenavWidth = signal<number>(0);
  protected readonly $footerHeight = signal<number>(52);
  protected readonly $shellTopOffset = computed(() => (!this.environment.production || this.isSuperUserAndProduction || this.$preventLogin()) ? 26 : 0);

  private readonly _onNavigationEnd = effect(() => {
    const e = this.$navigationEnd();
    if (!e) return;
    const url = e.urlAfterRedirects;
    if (url.startsWith('/agent') && untracked(() => this.agentPanelService.$panelOpen())) {
      this.agentPanelService.closePanel();
    }
  });

  /**
   * Sync panel position when state, mode, or active tab changes.
   * Skips anchor measurement in pseudo mode (position is fixed by CSS).
   */
  private readonly _syncPanelPosition = effect(() => {
    const panelOpen = this.agentPanelService.$panelOpen();
    const mode = this.agentPanelService.$displayMode();
    // Track $activeTabId so the reveal cycle re-runs when the active tab changes.
    this.agentPanelService.$activeTabId();

    if (!panelOpen) {
      this.$panelPositionReady.set(false);
      this.wasPanelOpen = false;
      return;
    }

    const freshOpen = !this.wasPanelOpen;
    this.wasPanelOpen = true;

    // Double RAF: first waits for change detection, second ensures
    // shell-top / side-nav / agent-chat-footer are in the DOM before measuring.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.measureSidenavAndFooter();
        if (mode === 'popup') {
          // A fresh open always snaps back to the far-right default (whichever tab
          // launched it); an already-open window keeps whatever the user dragged/
          // resized it to, only re-clamped so it stays fully on screen.
          const current = untracked(() => this.$popupRect());
          this.$popupRect.set(freshOpen || !current ? this.computeDefaultPopupRect() : this.clampPopupRect(current));
        }

        // Reveal only after the geometry is committed in the previous frame. While
        // the panel is still hidden, .panel-hidden doesn't transition top/left/size,
        // so the rect snaps into place instantly; revealing a frame later means the
        // open animation is a pure vertical rise + fade rather than a horizontal slide.
        requestAnimationFrame(() => this.$panelPositionReady.set(true));
      });
    });
  });

  private _stateSubscription: Subscription;
  public state: any;

  public showFeed = false;
  public environment = ConfigService.environment;
  public friendlyEnvironment = DomainUtility.getFriendlyEnvironment(this.environment.environment as any);
  public buildVersion = this.environment.build === 'undefined-undefined' ? 'Dev Version' : this.environment.build;
  public accountUrl: string;
  public isSuperUserAndProduction = (this.currentUserService.role?.type === UserType.SystemAdministrator) && this.environment.production;
  protected $preventLogin = ConfigService.$preventLogin;

  public menuItems = [
    { label: 'View Profile', icon: 'fa fa-user', command: () => { window.open(this.accountUrl, '_blank') } },
    { label: '-', separator: true },
    { label: 'Log Off', icon: 'fa fa-sign-out', command: () => this.actions.logout(true) },
  ]


  constructor() {

    timer(0, 3 * 60 * 1000)
      .pipe(
        takeUntilDestroyed(),
        switchMap(() => this.versionService.hasUpdates()),
      )
      .subscribe(outdated => {
        if (!outdated) return;
        this.toasts.neutral('New Version Available', 'Please reload the page to get the latest version of WorkBuddy', [
          new Command({
            label: 'Reload Now',
            action: () => {
              this.versionService.reload();
            }
          }),
          new Command({
            label: 'Reload All Tabs',
            action: () => {
              this.versionService.reloadAll();
            }
          })
        ],
          { id: 'new-version', sticky: true },
        );
      });

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const prev = this.lastViewport;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        this.lastViewport = { vw, vh };
        const tooSmall = isViewportTooSmallForPopup();
        this.$viewportTooSmallForPopup.set(tooSmall);

        if (!this.agentPanelService.$panelOpen()) return;

        // Too small for a floating WINDOW to be usable — close it rather than let a cramped
        // window fight the app for space. The expanded surface is deliberately exempt: it
        // fills the screen, so it stays usable at any size (see $showBottomBar).
        if (tooSmall && this.agentPanelService.$displayMode() === 'popup') {
          this.agentPanelService.closePanel();
          return;
        }

        this.measureSidenavAndFooter();
        // Reflow the windowed panel so it stays anchored to whichever edge it sits nearest
        // (a bottom-right window stays bottom-right) instead of its fixed top/left letting
        // it drift as the viewport grows/shrinks.
        if (this.agentPanelService.$displayMode() === 'popup') {
          const rect = this.$popupRect();
          if (rect) this.$popupRect.set(this.reflowPopupRect(rect, prev, vw, vh));
        }
      });

  }

  async ngOnInit() {

    this._stateSubscription = this.actions.state$
      .subscribe(state => {
        this.state = state;
      });

    this.accountUrl = `${this.environment.jms.loginHref}/account`;
  }

  ngOnDestroy() {

    this._stateSubscription.unsubscribe();
    this.removeWindowGestureListeners?.();
  }

  public prepareAnimations(outlet: RouterOutlet) {

    return outlet.activatedRoute.url;
  }

  // ─── Agent V2 handlers ──────────────────────────────────────────────────────

  private measureSidenavAndFooter(): void {

    const sideNav = document.querySelector('side-nav, inline-side-nav') as HTMLElement | null;
    if (sideNav) this.$sidenavWidth.set(sideNav.clientWidth);
    const footer = document.querySelector('agent-chat-footer') as HTMLElement | null;
    if (footer) this.$footerHeight.set(footer.clientHeight);
  }

  // ─── Windowed panel geometry ────────────────────────────────────────────────

  /** Far-right default: fixed size (clamped to the viewport), pinned to the bottom-right
   *  above the footer — the position every fresh open lands in, independent of the tab. */
  private computeDefaultPopupRect(): PopupRect {

    const footer = this.$footerHeight();
    const top0 = this.$shellTopOffset();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(POPUP_DEFAULT_WIDTH, vw - 2 * POPUP_OPEN_MARGIN);
    const availableHeight = vh - footer - top0 - 2 * POPUP_EDGE_GAP;
    const height = Math.min(POPUP_DEFAULT_HEIGHT, Math.max(POPUP_MIN_HEIGHT, availableHeight));
    const left = Math.max(POPUP_EDGE_GAP, vw - width - POPUP_OPEN_MARGIN);
    const top = Math.max(top0 + POPUP_EDGE_GAP, vh - footer - height);
    return { top, left, width, height };
  }

  /** Constrain a rect to the min size and the on-screen area (below the shell top bar,
   *  above the footer, inside the horizontal edges). */
  private clampPopupRect(rect: PopupRect): PopupRect {

    const footer = this.$footerHeight();
    const top0 = this.$shellTopOffset();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(Math.max(rect.width, POPUP_MIN_WIDTH), vw - 2 * POPUP_EDGE_GAP);
    const height = Math.min(Math.max(rect.height, POPUP_MIN_HEIGHT), vh - top0 - footer - 2 * POPUP_EDGE_GAP);
    const left = Math.min(Math.max(rect.left, POPUP_EDGE_GAP), vw - width - POPUP_EDGE_GAP);
    const top = Math.min(Math.max(rect.top, top0 + POPUP_EDGE_GAP), vh - footer - height - POPUP_EDGE_GAP);
    return { top, left, width, height };
  }

  /** Reposition the window for a new viewport size by preserving the gap to whichever
   *  edge it sits closest to. A bottom-right window keeps its bottom-right gap (so it
   *  tracks the corner as the browser resizes); a top-left window keeps its top-left
   *  offset. The result is clamped to stay fully on screen. */
  private reflowPopupRect(rect: PopupRect, prev: { vw: number; vh: number }, vw: number, vh: number): PopupRect {

    const rightGap = prev.vw - (rect.left + rect.width);
    const bottomGap = prev.vh - (rect.top + rect.height);
    const left = rect.left <= rightGap ? rect.left : vw - rect.width - rightGap;
    const top = rect.top <= bottomGap ? rect.top : vh - rect.height - bottomGap;
    return this.clampPopupRect({ ...rect, left, top });
  }

  /** Live geometry of the wrapper element — the fallback base for a gesture if the
   *  rect signal is somehow unset (it normally holds the default from the open cycle). */
  private measureWrapperRect(): PopupRect | null {

    const el = document.querySelector('.agent-panel-wrapper') as HTMLElement | null;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  /** Snap the windowed panel back to its far-right default position + size (animated). */
  protected onResetPopupPosition(): void {

    if (this.agentPanelService.$displayMode() !== 'popup') return;
    this.$popupRect.set(this.computeDefaultPopupRect());
  }

  /** Start a title-bar drag (emitted by the popup header). Moves the window with the
   *  pointer, clamped on screen, with easing suspended for the duration of the gesture. */
  protected onPopupDragStart(event: PointerEvent): void {

    // Only the windowed form is movable; the expanded surface is maximised.
    if (this.agentPanelService.$displayMode() !== 'popup') return;
    const base = this.$popupRect() ?? this.measureWrapperRect();
    if (!base) return;
    const start = { ...base };
    const startX = event.clientX;
    const startY = event.clientY;

    this.beginWindowGesture(move => {
      this.$popupRect.set(this.clampPopupRect({ ...start, left: start.left + (move.clientX - startX), top: start.top + (move.clientY - startY) }));
    });
  }

  /** Start an edge/corner resize. Grows/shrinks from the grabbed side, holding the
   *  opposite side fixed and honouring the minimum size, then clamps on screen. */
  protected onResizeStart(event: PointerEvent, dir: ResizeDir): void {

    event.preventDefault();
    event.stopPropagation();
    const base = this.$popupRect() ?? this.measureWrapperRect();
    if (!base) return;
    const start = { ...base };
    const startX = event.clientX;
    const startY = event.clientY;

    this.beginWindowGesture(move => {
      const dx = move.clientX - startX;
      const dy = move.clientY - startY;
      let { top, left, width, height } = start;
      if (dir.includes('e')) width = start.width + dx;
      if (dir.includes('s')) height = start.height + dy;
      if (dir.includes('w')) { width = start.width - dx; left = start.left + dx; }
      if (dir.includes('n')) { height = start.height - dy; top = start.top + dy; }
      // When shrinking from the west/north edge, pin the moving edge at the min so the
      // opposite (east/south) edge stays put instead of the window sliding past it.
      if (width < POPUP_MIN_WIDTH && dir.includes('w')) left = start.left + (start.width - POPUP_MIN_WIDTH);
      if (height < POPUP_MIN_HEIGHT && dir.includes('n')) top = start.top + (start.height - POPUP_MIN_HEIGHT);
      this.$popupRect.set(this.clampPopupRect({ top, left, width, height }));
    });
  }

  /** Shared pointer-gesture loop for drag + resize: listens on the document, marks the
   *  window as interacting (so its transition is suspended), and tears down on release. */
  private beginWindowGesture(onMove: (event: PointerEvent) => void): void {

    // Guard against a stuck prior gesture (e.g. a missed pointerup).
    this.removeWindowGestureListeners?.();
    this.$windowInteracting.set(true);

    const move = (event: PointerEvent): void => {
      event.preventDefault();
      // Flipped on the first real movement rather than on pointerdown, so a plain click on
      // the title bar doesn't flash the reset overlay.
      this.$windowDragging.set(true);
      onMove(event);
    };
    const up = (): void => {
      this.removeWindowGestureListeners?.();
    };
    this.removeWindowGestureListeners = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      this.removeWindowGestureListeners = null;
      this.$windowInteracting.set(false);
      this.$windowDragging.set(false);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
  }

  protected onAgentTabActivated(threadId: string): void {

    this.agentPanelService.toggleTab(threadId);
  }

  protected onAgentTabClosed(threadId: string): void {

    this.agentPanelService.closeTab(threadId);
  }

  protected onAgentNewChat(): void {

    this.agentPanelService.toggleTab(RESERVED_TAB_ID.NEW_CHAT);
  }

  protected onAgentThreadSelected(event: { threadId: string; title: string }): void {

    this.agentPanelService.reopenFromHistory(event.threadId, event.title);
  }

  protected onAgentPopupClose(): void {

    this.agentPanelService.closePanel();
  }

}
